import { useReportLeadErrorMutation } from "@/api/leads.api";
import { Button } from "@/components/generic/Button";
import { useLeadNavigation } from "@/providers/LeadNavigationProvider";
import { Route } from "@/routes/player/route";
import { usePhoneNumber } from "@/services/phone.services";
import { Lead, LeadFeedback } from "@/types/campaign";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import { HeartHandshake } from "lucide-react";
import { useMemo, useState } from "react";
import { Label, Text } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import z from "zod";
import { Checkbox } from "../generic/Checkbox";
import { Heading } from "../generic/Heading";
import { Modal } from "../generic/Modal";
import { Popover } from "../generic/Popover";
import { CustomRadio } from "../generic/RadioGroup";
import { TextField } from "../generic/TextField";

const FEEDBACK_LABELS: Partial<Record<LeadFeedback, string>> = {
  [LeadFeedback.WrongTargetGroup]: "Forkert målgruppe",
  [LeadFeedback.ContactNoLongerExists]: "Kontakten eksisterer ikke længere",
  [LeadFeedback.Other]: "Andet",
};

const PHONE_LABEL_OVERRIDES: Partial<Record<LeadFeedback, string>> = {
  [LeadFeedback.BadPhonenumber]: "Telefonnummeret virker ikke",
  [LeadFeedback.WrongTargetGroup]:
    "Telefonnummeret er ikke til det angivne firma",
  [LeadFeedback.Other]: "Andet",
};

const SINGLE_CONTACT: Partial<Record<LeadFeedback, string>> = {
  [LeadFeedback.BadPhonenumber]: "Telefonnummeret virker ikke",
  [LeadFeedback.InvalidContact]: "Kontaktpersonens oplysninger er forkerte",
  [LeadFeedback.Other]: "Andet",
};

const formSchema = z
  .object({
    feedbackType: z.string().min(1, "Vælg venligst en fejltype"),
    context: z.string().optional(),
    disqualified: z.boolean(),
    refund: z.boolean().nullable(),
  })
  .refine(
    (data) => {
      if (data.feedbackType === LeadFeedback.Other) {
        return data.context && data.context.trim().length > 0;
      }
      return true;
    },
    {
      message: "Angiv venligst yderligere oplysninger",
      path: ["context"],
    },
  );

type FormValues = z.infer<typeof formSchema>;

type ModalType = "error" | "phone" | "single_phone";

interface IReportErrorDialog {
  modalType: ModalType;
  selectedPhoneNumber?: string;
  lead: Lead;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  popoverProps?: Omit<
    React.ComponentProps<typeof Popover>,
    "isOpen" | "onOpenChange" | "children"
  >;
}

export function ReportErrorDialog({
  modalType,
  selectedPhoneNumber,
  isOpen,
  onOpenChange,
  lead,
  popoverProps,
}: IReportErrorDialog) {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isValid },
  } = useForm<FormValues>({
    defaultValues: {
      feedbackType: "",
      context: "",
      disqualified: false,
      refund: null,
    },
    resolver: zodResolver(formSchema),
  });

  const context = Route.useRouteContext();
  const [openRefundModal, setOpenRefundModal] = useState(false);

  const { triggerNextLead } = useLeadNavigation();

  const companyLassoId = useMemo(
    () =>
      context.view.type === "company"
        ? context.view.company.lassoId
        : context.view.simpleLead.lassoId,
    [context.view],
  );

  const { data: phoneNumbers } = usePhoneNumber(
    lead.id.toString() ?? "",
    companyLassoId,
  );

  const getLabel = (feedbackType: LeadFeedback): string => {
    if (modalType === "phone" && PHONE_LABEL_OVERRIDES[feedbackType]) {
      return PHONE_LABEL_OVERRIDES[feedbackType]!;
    }
    if (modalType === "single_phone" && SINGLE_CONTACT[feedbackType]) {
      return SINGLE_CONTACT[feedbackType]!;
    }
    return FEEDBACK_LABELS[feedbackType] ?? "";
  };

  const availableOptions = () => {
    switch (modalType) {
      case "phone":
        return [
          LeadFeedback.BadPhonenumber,
          LeadFeedback.WrongTargetGroup,
          LeadFeedback.Other,
        ];
      case "single_phone":
        return [
          LeadFeedback.BadPhonenumber,
          LeadFeedback.InvalidContact,
          LeadFeedback.Other,
        ];

      default:
        return [LeadFeedback.WrongTargetGroup, LeadFeedback.Other];
    }
  };

  const FEEDBACK_OPTIONS = availableOptions().map((value) => ({
    value,
    label: getLabel(value),
  }));

  const phoneNumber = useMemo(() => {
    return phoneNumbers?.numbers.find((ph) => ph.callable);
  }, [phoneNumbers]);

  const { mutate: reportError, isPending } = useReportLeadErrorMutation();

  const onSubmit = async (form: FormValues) => {
    if (form.disqualified && form.refund === null) {
      setOpenRefundModal(true);
      onOpenChange(false);
      return;
    }

    const refund = form.refund ?? false;

    const getContextValue = () => {
      if (modalType === "error") {
        return phoneNumber?.phoneNumber
          ? String(phoneNumber.phoneNumber)
          : undefined;
      } else if (modalType === "phone") {
        return selectedPhoneNumber ? String(selectedPhoneNumber) : undefined;
      }
      return undefined;
    };

    reportError(
      {
        leadId: Number(lead.id) || 0,
        data: {
          feedback: form.feedbackType as LeadFeedback,
          lassoId: companyLassoId,
          context: getContextValue(),
          disqualified: form.disqualified,
          comment: form.context || undefined,
          refund: refund,
        },
      },
      {
        onSuccess: () => {
          toast.success("Feedback sendt");
          if (refund) triggerNextLead(true);
          onOpenChange(false);
          setValue("refund", null);
          reset();
        },
        onError: (error) => {
          setValue("refund", null);
          console.error("Error reporting lead:", error);
          toast.error("Noget gik galt");
        },
      },
    );
  };

  const feedbackType = watch("feedbackType");

  const otherFeedbackSelected = useMemo(() => {
    return feedbackType === LeadFeedback.Other;
  }, [feedbackType]);

  return (
    <>
      <Popover
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        className="rounded-sm outline-none"
        {...popoverProps}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            transition: {
              duration: 0.3,
              ease: "easeOut",
            },
          }}
          exit={{
            opacity: 0,
            transition: { duration: 0.2, ease: "easeIn" },
          }}
        >
          <form
            className="bg-theme-glass text-theme-on-background w-[426px] rounded-sm p-10"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex flex-col gap-3">
              {modalType === "phone" ? (
                <Heading className="text-theme-on-background-variant text-xs font-medium">
                  Hvad er der galt med nummeret?
                </Heading>
              ) : (
                <Heading className="text-theme-on-background-variant text-xs font-medium">
                  Hvad er der galt med leadet?
                </Heading>
              )}

              {FEEDBACK_OPTIONS.map(({ value, label }) => {
                const isSelected = watch("feedbackType") === value;

                return (
                  <Label
                    key={value}
                    className="border-theme-outline-variant hover:bg-theme-on-background/3 relative flex cursor-pointer items-center rounded-sm border transition-colors"
                  >
                    <div className="h-full p-3">
                      <CustomRadio
                        value={value}
                        isSelected={isSelected}
                        onChange={(value: string) =>
                          setValue("feedbackType", value as LeadFeedback, {
                            shouldValidate: true,
                          })
                        }
                      />
                    </div>
                    <div className="border-theme-outline-variant border-l p-4">
                      <Text className="text-xs font-medium text-wrap">
                        {label}
                      </Text>
                    </div>
                  </Label>
                );
              })}

              <Controller
                control={control}
                name="context"
                render={({ field }) => (
                  <div>
                    <TextField
                      {...field}
                      className={twMerge(
                        "outline-theme-outline-variant w-full rounded-md outline-1 transition-all duration-600",
                        otherFeedbackSelected &&
                          "outline-theme-on-glass-variant outline-1",
                      )}
                      inputStyles="border-0"
                      aria-label="Angiv yderligere oplysninger"
                      placeholder="Angiv yderligere oplysninger"
                      control={control}
                      type="text"
                    />
                  </div>
                )}
              />
              {/* Will add in next versions */}
              <Controller
                control={control}
                name="disqualified"
                render={({ field }) => (
                  <div className="my-4 flex items-center gap-2">
                    <Checkbox
                      onChange={field.onChange}
                      isSelected={field.value}
                    />
                    <Heading className="text-theme-on-background-variant text-xs font-medium">
                      Jeg ønsker ikke at gøre brug af dette lead
                    </Heading>
                  </div>
                )}
              />
            </div>

            <div className="mt-6 flex justify-center gap-3">
              <Button
                type="submit"
                animate={true}
                isDisabled={!isValid || isPending}
              >
                Indsend
              </Button>
            </div>
          </form>
        </motion.div>
      </Popover>

      <Modal
        isDismissable={false}
        isOpen={openRefundModal}
        onOpenChange={setOpenRefundModal}
        size="lg"
        icon={
          <HeartHandshake size={40} className="text-theme-gradient-from/90" />
        }
      >
        {({ close }) => (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <Text className="text-theme-on-background-variant text-center text-sm">
                Tak for din feedback.
                <br />
                <br />
                Vi fjerner nu dette lead fra dine lister. Ønsker du at få
                refunderet{" "}
                {(lead.creditsUsed ?? 0) +
                  (lead.creditsUsedOnContact ?? 0)}{" "}
                credit(s) for dette lead?
              </Text>
            </div>
            <div className="flex justify-center gap-3">
              <Button
                intent="secondary"
                onPress={() => {
                  setValue("refund", false);
                  close();
                  handleSubmit(onSubmit)();
                }}
              >
                Nej tak
              </Button>
              <Button
                intent="primary"
                onPress={() => {
                  setValue("refund", true);
                  close();
                  handleSubmit(onSubmit)();
                }}
              >
                Refunder
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
